import { useCallback, useEffect, useState } from "react";
import api from "../../lib/api";
import { formatFechaHora } from "../../lib/dates";
import { Button } from "../../components/ui/Button";
import { ConfirmDialog } from "../../components/ui/ConfirmDialog";
import { Input } from "../../components/ui/Input";
import { Modal } from "../../components/ui/Modal";

interface ApiKeyRow {
  id: number;
  nombre: string;
  key_preview: string;
  activa: boolean;
  created_at: string;
  last_used_at: string | null;
}

export function AseguradoraApiPage() {
  const [keys, setKeys] = useState<ApiKeyRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [nombre, setNombre] = useState("");
  const [creating, setCreating] = useState(false);
  const [secret, setSecret] = useState<string | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [acknowledged, setAcknowledged] = useState(false);
  const [copied, setCopied] = useState(false);
  const [revokeId, setRevokeId] = useState<number | null>(null);

  const loadKeys = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get<ApiKeyRow[]>("/aseguradora/api-keys");
      setKeys(Array.isArray(res.data) ? res.data : []);
      setError(null);
    } catch {
      setKeys([]);
      setError("No se han podido cargar las API keys.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void loadKeys(); }, [loadKeys]);

  const createKey = async () => {
    const value = nombre.trim();
    if (!value) return;
    setCreating(true);
    try {
      const res = await api.post<{ api_key: string; key_preview: string }>("/aseguradora/api-keys", { nombre: value });
      setCreateOpen(false);
      setSecret(res.data.api_key);
      setPreview(res.data.key_preview);
      setAcknowledged(false);
      setCopied(false);
      await loadKeys();
    } catch (err: unknown) {
      setError((err as { response?: { data?: { error?: string } } })?.response?.data?.error ?? "No se ha podido crear la API key.");
    } finally {
      setCreating(false);
    }
  };

  const revokeKey = async () => {
    if (revokeId == null) return;
    try {
      await api.delete(`/aseguradora/api-keys/${revokeId}`);
      setRevokeId(null);
      await loadKeys();
    } catch (err: unknown) {
      setError((err as { response?: { data?: { error?: string } } })?.response?.data?.error ?? "No se ha podido revocar la API key.");
    }
  };

  const copyKey = async () => {
    if (!secret) return;
    try {
      await navigator.clipboard.writeText(secret);
      setCopied(true);
    } catch {
      setError("No se ha podido copiar la API key. Cópiala manualmente.");
    }
  };

  return (
    <div className="mx-auto max-w-4xl space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-[#2C2C2C]">Integración API</h1>
        <p className="mt-1 text-sm text-gray-600">Consulta asegurados y descarga certificados desde vuestra plataforma.</p>
      </div>
      {error && <p className="text-sm text-red-600">{error}</p>}

      <section className="space-y-4 rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="text-lg font-medium text-[#2C2C2C]">API keys</h2>
            <p className="text-sm text-gray-600">Cada clave identifica una integración y puede revocarse en cualquier momento.</p>
          </div>
          <Button onClick={() => { setNombre(""); setCreateOpen(true); }}>Generar API key</Button>
        </div>
        {loading ? <p className="text-sm text-gray-500">Cargando…</p> : keys.length === 0 ? (
          <p className="text-sm text-gray-600">No hay API keys creadas.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="border-b text-left text-gray-500"><th className="pb-2 pr-3">Nombre</th><th className="pb-2 pr-3">Clave</th><th className="pb-2 pr-3">Último uso</th><th className="pb-2 pr-3">Estado</th><th className="pb-2">Acciones</th></tr></thead>
              <tbody>{keys.map((key) => <tr key={key.id} className="border-b border-gray-100 last:border-0"><td className="py-2 pr-3">{key.nombre}</td><td className="py-2 pr-3 font-mono text-xs">{key.key_preview}</td><td className="py-2 pr-3 text-gray-600">{key.last_used_at ? formatFechaHora(key.last_used_at) : "—"}</td><td className="py-2 pr-3">{key.activa ? "Activa" : "Revocada"}</td><td className="py-2">{key.activa && <Button size="sm" variant="danger" onClick={() => setRevokeId(key.id)}>Revocar</Button>}</td></tr>)}</tbody>
            </table>
          </div>
        )}
      </section>

      <section className="space-y-3 rounded-lg border border-gray-200 bg-white p-6 shadow-sm text-sm text-gray-700">
        <h2 className="text-lg font-medium text-[#2C2C2C]">Uso de la API</h2>
        <p>Envía la clave en la cabecera <code className="rounded bg-gray-100 px-1">X-API-Key</code>.</p>
        <p className="break-all font-medium">POST /api/aseguradora/api/asegurados/buscar</p>
        <pre className="overflow-x-auto rounded-lg bg-gray-900 p-4 text-xs text-gray-100">{`{
  "nif": "12345678A",
  "nombre": "Nombre",
  "apellidos": "Apellidos",
  "fecha_nacimiento": "DD/MM/AAAA"
}`}</pre>
        <p>La respuesta contiene las mismas fichas de asegurado y datos de cobertura disponibles en el portal. Para un certificado disponible: <code className="break-all rounded bg-gray-100 px-1">GET /api/aseguradora/api/asegurados/:pilotoId/certificado</code>.</p>
      </section>

      <section className="space-y-3 rounded-lg border border-gray-200 bg-white p-6 text-sm text-gray-700">
        <h2 className="text-lg font-medium">API v2 · Consultas ampliadas</h2>
        <p>Las claves requieren permisos asignados por Circuit Cover para cada función. Con la cartera automática activada para esta integración, se incluyen todos los organizadores y modalidades de accidentes en circuito, actuales y futuros.</p>
        <p><code>POST /api/aseguradora/api/v2/asegurados/buscar</code></p>
        <pre className="overflow-x-auto rounded-lg bg-gray-900 p-4 text-xs text-gray-100">{`{
  "apellidos": "García Pérez",
  "fecha_nacimiento": "15/06/1990",
  "tipo": "anual",
  "modalidad_id": 1,
  "limite": 100
}`}</pre>
        <p>Se puede buscar solo por NIF, nombre, apellidos o fecha de nacimiento. Los campos enviados se combinan. El NIF se compara completo; nombres y apellidos admiten fragmentos sin distinguir mayúsculas ni acentos. Se conserva la Ñ.</p>
        <p>Filtros de cobertura: <code>inicio_seguro</code> (fecha exacta), <code>fecha_desde</code> y <code>fecha_hasta</code> (coberturas que coinciden con el periodo), <code>tipo</code> (anual o diario) y <code>modalidad_id</code>. Fechas: DD/MM/AAAA o YYYY-MM-DD, en horario de Madrid.</p>
        <p><code>GET /api/aseguradora/api/v2/organizadores</code> devuelve código, nombre y CIF de los organizadores autorizados.</p>
        <p><code>GET /api/aseguradora/api/v2/anuales?fecha_desde=2026-01-01&amp;fecha_hasta=2026-12-31&amp;limite=100</code> devuelve coberturas anuales individuales y requiere permiso propio.</p>
        <p>Las respuestas incluyen <code>data</code> y <code>paginacion.siguiente_cursor</code>. Para continuar, repite los mismos filtros enviando ese valor como <code>cursor</code>. Máximo 200 registros por página y 60 consultas por minuto por clave.</p>
        <p>Cada ficha incluye número de póliza, modalidad, tipo, estado, fechas, NIF en mayúsculas y nombres originales junto a sus campos normalizados. Un estado de pago o un PDF disponible no confirma por sí solo la vigencia de la cobertura.</p>
        <p>Certificados autorizados: <code>GET /api/aseguradora/api/v2/asegurados/:pilotoId/certificado</code>.</p>
      </section>

      <Modal isOpen={createOpen} onClose={() => setCreateOpen(false)} title="Generar API key" footer={null}>
        <div className="space-y-4"><Input label="Nombre" value={nombre} onChange={(e) => setNombre(e.target.value)} placeholder="Ej. Integración expedientes" /><div className="flex justify-end gap-2"><Button variant="secondary" onClick={() => setCreateOpen(false)}>Cancelar</Button><Button loading={creating} disabled={!nombre.trim()} onClick={() => void createKey()}>Generar</Button></div></div>
      </Modal>

      <Modal isOpen={secret != null} onClose={() => { if (acknowledged) setSecret(null); }} title="API key generada" footer={null}>
        {secret && <div className="space-y-4"><p className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">Copia esta API key ahora. No volverás a verla.</p>{preview && <p className="text-xs text-gray-500">Preview: {preview}</p>}<div className="flex flex-col gap-2 sm:flex-row"><input readOnly value={secret} className="flex-1 rounded-lg border border-gray-300 bg-gray-50 px-3 py-2 font-mono text-sm" /><Button variant="secondary" onClick={() => void copyKey()}>{copied ? "✓ Copiado" : "Copiar"}</Button></div><label className="flex items-start gap-2 text-sm"><input type="checkbox" className="mt-1" checked={acknowledged} onChange={(e) => setAcknowledged(e.target.checked)} />He copiado mi API key</label><div className="flex justify-end"><Button disabled={!acknowledged} onClick={() => { setSecret(null); setPreview(null); }}>Cerrar</Button></div></div>}
      </Modal>

      <ConfirmDialog isOpen={revokeId != null} onClose={() => setRevokeId(null)} onConfirm={() => void revokeKey()} title="Revocar API key" message="La integración que use esta clave dejará de funcionar. ¿Continuar?" confirmLabel="Revocar" variant="danger" />
    </div>
  );
}
